import { Mutation } from "@apollo/client/react/components";
import { SearchAlgoliaIndex } from "@govlaunch/algolia";
import { Button, Modal, ModalClose } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
/* eslint-disable-next-line */
import { MultiVendorSelect } from "@govlaunch/select";
import moment from "moment";
import Link from "next/link";
import React, { createRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useToggle } from "react-use";
import useClipboard from "~/lib/hooks/useClipboard";
import useShowMoreCounter from "~/lib/hooks/useShowMoreCounter";
import CancelGroupRequest from "~/lib/mutations/CancelGroupRequest";
import InviteVendorsToGroup from "~/lib/mutations/InviteVendorsToGroup";
import {
  ICancelGroupRequestMutation,
  ICancelGroupRequestMutationVariables,
} from "~/lib/mutations/__generated__/CancelGroupRequest.generated";
import {
  IInviteVendorsToGroupMutation,
  IInviteVendorsToGroupMutationVariables,
} from "~/lib/mutations/__generated__/InviteVendorsToGroup.generated";
import { IGroupQuery } from "~/lib/queries/__generated__/GroupQuery.generated";
import filterByTypename from "~/lib/utils/filterByTypename";
import getGovlaunchBaseUrl from "~/lib/utils/getGovlaunchBaseUrl";
import IntlRelativeTime from "~/src/shared/components/IntlRelativeTime";
import { ICompany, IGroup } from "~/types/types";
import { useSelfie } from "~/components/auth/Auth";
import ClipboardIcon from "~/components/icons/ClipboardIcon";
import mediaQueries from "~/components/mediaQueries";
import { Margin } from "~/components/spacings";
import Tooltip from "~/components/Tooltip";

interface IGroupVendorProps {
  group: Pick<IGroup, "_id" | "name" | "slug" | "viewerIsMember"> & {
    vendors: Pick<ICompany, "_id" | "name" | "slug" | "logo">[];
    pendingRequests: NonNullable<IGroupQuery["groupBySlug"]>["pendingRequests"];
  };
}

export default function GroupVendors({ group }: IGroupVendorProps) {
  const [isOpen, toggle] = useToggle(false);
  const [isShowingPendingInvites, togglePendingInvites] = useToggle(false);
  const { vendors } = group;
  const { hasMore, offset, showMore } = useShowMoreCounter(vendors.length, 18);
  const requests = group.pendingRequests.filter(filterByTypename("VendorGroupRequest"));

  if (vendors.length === 0 && requests.length === 0 && !group.viewerIsMember) {
    return null;
  }

  return (
    <div>
      <VendorInvitationModal group={group} isOpen={isOpen} onRequestClose={toggle} />

      {vendors.length === 0 && requests.length === 0 && group.viewerIsMember ? (
        <Button
          size="large"
          theme="secondary"
          color={palette.primary}
          onClick={toggle}
          data-intercom-selector="Group Invite Vendors Button"
          css={{
            background: "#fff",
            width: "100%",
          }}
        >
          <FormattedMessage defaultMessage="Invite Vendors" id="xvrc9Z" />
        </Button>
      ) : (
        <div
          data-intercom-target="Group Vendors Box"
          css={{
            borderRadius: 5,
            border: `1px solid ${palette.lightestGray}`,
            background: "#fff",
          }}
        >
          <div
            css={{
              padding: 16,
              display: "flex",
              borderBottom: `1px solid ${palette.lightestGray}`,
            }}
          >
            <span
              css={{
                fontSize: 12,
                fontWeight: 500,
                color: palette.sealBlue,
                textTransform: "uppercase",
              }}
            >
              {group.vendors.length === 0 ? (
                <FormattedMessage defaultMessage="Vendors" id="RilevA" />
              ) : (
                <FormattedMessage
                  defaultMessage="Vendors {vendorsCount}"
                  id="TGPwwb"
                  values={{ vendorsCount: group.vendors.length }}
                />
              )}
            </span>

            {group.viewerIsMember && (
              <span
                role="button"
                css={{
                  marginLeft: "auto",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  color: palette.primary,
                  cursor: "pointer",
                  fontSize: 14,
                }}
                onClick={toggle}
              >
                <FormattedMessage defaultMessage="Invite" id="hYOE+U" />
              </span>
            )}
          </div>

          <div>
            <div
              css={{
                padding: 16,
              }}
            >
              {vendors.length > 0 && (
                <div
                  css={{
                    display: "grid",
                    gridTemplateColumns: "repeat(6, 1fr)",
                    gridGap: 12,
                  }}
                >
                  {vendors.slice(0, offset).map((vendor) => (
                    <Tooltip
                      key={vendor._id}
                      backgroundColor={palette.innovatorBlue}
                      boxStyle={{
                        padding: "4px 8px",
                        border: "none",
                        borderRadius: 4,
                        boxShadow: "0 10px 40px 0 rgba(34, 35, 40, 0.2)",
                      }}
                      arrowSize={8}
                      render={() => (
                        <span
                          css={{
                            color: palette.white,
                            fontWeight: 500,
                            fontSize: 13,
                            lineHeight: 1.33,
                            letterSpacing: 1,
                          }}
                        >
                          {vendor.name}
                        </span>
                      )}
                    >
                      {({ innerRef, bind }) => (
                        <Link
                          href="/vendors/[vendorSlug]"
                          as={`/vendors/${vendor.slug}`}
                          passHref={true}
                          legacyBehavior={true}
                        >
                          <a
                            {...bind}
                            ref={innerRef}
                            css={{
                              position: "relative",
                              display: "block",
                              color: "unset",
                              textDecoration: "none",
                            }}
                          >
                            <ProfilePicture
                              size={42}
                              image={vendor.logo}
                              name={vendor.name}
                              css={{
                                display: "block",
                              }}
                            />
                          </a>
                        </Link>
                      )}
                    </Tooltip>
                  ))}
                </div>
              )}

              {requests.length > 0 && (
                <>
                  <span
                    role="button"
                    onClick={togglePendingInvites}
                    css={{
                      display: "block",
                      marginTop: group.vendors.length > 0 ? 16 : 0,
                      cursor: "pointer",
                      fontWeight: 500,
                      color: palette.red,
                      fontSize: 14,
                    }}
                  >
                    <FormattedMessage
                      defaultMessage="{count} invite pending"
                      id="L599T6"
                      values={{ count: requests.length }}
                    />
                  </span>
                  {isShowingPendingInvites && (
                    <Modal ariaHideApp={false} isOpen={true} onRequestClose={togglePendingInvites}>
                      <ModalClose onClick={togglePendingInvites} />
                      <div
                        css={mediaQueries({
                          width: [350, 350, 580, 580],
                          padding: 32,
                        })}
                      >
                        <PendingInvites group={group} groupRequests={requests} />
                      </div>
                    </Modal>
                  )}
                </>
              )}
            </div>

            {hasMore && (
              <button
                type="button"
                onClick={showMore}
                css={{
                  width: "100%",
                  background: "none",
                  outline: 0,
                  fontSize: 14,
                  fontWeight: 500,
                  height: 48,
                  textAlign: "center",
                  textTransform: "uppercase",
                  color: palette.sealBlue,
                  border: 0,
                  display: "block",
                  lineHeight: "48px",
                  borderTop: `1px solid ${palette.lightestGray}`,
                  cursor: "pointer",
                }}
              >
                <FormattedMessage defaultMessage="Show more" id="aWpBzj" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface IVendorInvitationModal {
  group: Pick<IGroup, "_id" | "name"> & {
    vendors: Pick<ICompany, "_id" | "name" | "slug" | "logo">[];
    pendingRequests: NonNullable<IGroupQuery["groupBySlug"]>["pendingRequests"];
  };
  isOpen: boolean;
  onRequestClose: (arg?: any) => void;
}

function VendorInvitationModal({ group, isOpen, onRequestClose }: IVendorInvitationModal) {
  const intl = useIntl();
  const [selectedVendors, setSelectedVendors] = useState([]);

  if (!isOpen) {
    return null;
  }

  return (
    <Mutation<IInviteVendorsToGroupMutation, IInviteVendorsToGroupMutationVariables> mutation={InviteVendorsToGroup}>
      {(inviteVendors, { data }) => (
        <Modal
          ariaHideApp={false}
          isOpen={isOpen}
          onRequestClose={onRequestClose}
          destroyOnClose={true}
          overlayStyle={{
            zIndex: 1000,
          }}
          style={{
            overflow: "visible",
          }}
        >
          <ModalClose onClick={onRequestClose} />

          {!(data && data.inviteVendorsToGroup) ? (
            <div
              css={mediaQueries({
                width: [350, 350, 650, 650],
                padding: 32,
              })}
            >
              <div>
                <div>
                  <p
                    css={{
                      fontSize: 18,
                      fontWeight: "bold",
                      color: palette.darkGray,
                      margin: "0 0 4px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <span css={{ marginRight: 4 }}>
                      <FormattedMessage defaultMessage="Invite Vendors" id="xvrc9Z" />
                    </span>
                    <svg viewBox="0 0 64 64" width={22} height={22}>
                      <path d="M1.652 19.564h17V62h-17zM44.862 19.564H62V62H44.862z" fill="#5aaae0" />
                      <path d="M18.652 2h26.21v60h-26.21z" fill="#7dc7ff" />
                      <path
                        d="M33.912 48.566a3.923 3.923 0 0 0 1.894-3.343 4 4 0 0 0-8 0 3.923 3.923 0 0 0 1.893 3.343 5.922 5.922 0 0 0-3.893 5.539V62h12v-7.895a5.922 5.922 0 0 0-3.894-5.539z"
                        fill="#f73445"
                      />
                      <g fill="#359ed8">
                        <path d="M22.684 23.512H24.7v1.973h-2.016zM22.684 27.46H24.7v1.973h-2.016zM22.684 31.407H24.7v1.973h-2.016zM22.684 35.354H24.7v1.974h-2.016zM26.717 23.512h2.016v1.973h-2.016zM26.717 27.46h2.016v1.973h-2.016zM26.717 31.407h2.016v1.973h-2.016zM26.717 35.354h2.016v1.974h-2.016zM30.749 23.512h2.016v1.973H30.75zM30.749 27.46h2.016v1.973H30.75zM30.749 31.407h2.016v1.973H30.75zM30.749 35.354h2.016v1.974H30.75zM34.781 23.512h2.017v1.973H34.78zM26.717 19.564h2.016v1.974h-2.016zM30.749 19.564h2.016v1.974H30.75zM34.781 19.564h2.017v1.974H34.78zM34.781 27.46h2.017v1.973H34.78zM34.781 31.407h2.017v1.973H34.78zM34.781 35.354h2.017v1.974H34.78zM38.814 23.512h2.016v1.973h-2.016zM38.814 27.46h2.016v1.973h-2.016zM38.814 31.407h2.016v1.973h-2.016zM38.814 35.354h2.016v1.974h-2.016z" />
                      </g>
                      <path
                        d="M13.47 52.485a2.93 2.93 0 0 0 1.166-2.328 3 3 0 0 0-6 0A2.93 2.93 0 0 0 9.8 52.485a4.93 4.93 0 0 0-3.165 4.58V62h10v-4.934a4.93 4.93 0 0 0-3.166-4.581z"
                        fill="#f7f769"
                      />
                      <path
                        d="M22.543 49.524a2.93 2.93 0 0 0 1.165-2.327 3 3 0 0 0-6 0 2.93 2.93 0 0 0 1.166 2.327 4.93 4.93 0 0 0-3.166 4.581V62h10v-7.895a4.93 4.93 0 0 0-3.165-4.581z"
                        fill="#ffc943"
                      />
                      <path
                        d="M53.794 52.485a2.93 2.93 0 0 0 1.166-2.328 3 3 0 0 0-6 0 2.93 2.93 0 0 0 1.165 2.328 4.93 4.93 0 0 0-3.166 4.58V62h10v-4.934a4.93 4.93 0 0 0-3.165-4.581z"
                        fill="#f7f769"
                      />
                      <path
                        d="M44.72 49.524a2.93 2.93 0 0 0 1.167-2.327 3 3 0 0 0-6 0 2.93 2.93 0 0 0 1.165 2.327 4.93 4.93 0 0 0-3.165 4.581V62h10v-7.895a4.93 4.93 0 0 0-3.166-4.581z"
                        fill="#ffc943"
                      />
                      <path
                        d="M49.903 26.472h2.016v15.79h-2.016zM55.951 26.472h2.017v15.79H55.95zM5.546 26.472h2.017v15.79H5.546zM11.595 26.472h2.016v15.79h-2.016z"
                        fill="#005889"
                      />
                      <path d="M23.692 4.76h16.13v9.87h-16.13z" fill="#359ed8" />
                    </svg>
                  </p>
                  <p
                    css={{
                      fontSize: 14,
                      margin: 0,
                      color: palette.mediumGray,
                    }}
                  >
                    <FormattedMessage
                      defaultMessage="They’ll be able to access {groupName}"
                      id="RBJjrh"
                      values={{ groupName: group.name }}
                    />
                  </p>
                </div>

                <Margin mt={28}>
                  <SearchAlgoliaIndex index="companies">
                    {({ results: vendors, searchValue: inputValue, onChange: onInputValueChange }: any) => (
                      <MultiVendorSelect
                        selectableVendors={vendors.filter(({ _id }: { _id: string }) => {
                          return !group.vendors.find((vendor) => vendor._id === _id);
                        })}
                        css={{
                          minWidth: 300,
                        }}
                        vendors={selectedVendors}
                        inputValue={inputValue}
                        onInputValueChange={onInputValueChange}
                        onChange={setSelectedVendors}
                        placeholder={intl.formatMessage({ defaultMessage: "Search Vendors", id: "6T8YYS" })}
                      />
                    )}
                  </SearchAlgoliaIndex>
                </Margin>

                <Margin mt={28}>
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      color={palette.innovatorBlue}
                      textColor={palette.white}
                      onClick={() => {
                        inviteVendors({
                          variables: {
                            groupId: group._id,
                            vendorIds: selectedVendors.map(({ objectID }) => objectID),
                          },
                        });
                      }}
                    >
                      <FormattedMessage defaultMessage="Invite Vendors" id="xvrc9Z" />
                    </Button>
                    <Margin ml={8}>
                      <a
                        href="#"
                        onClick={onRequestClose}
                        css={{
                          color: palette.mediumGray,
                          fontSize: 14,
                        }}
                      >
                        <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
                      </a>
                    </Margin>
                  </div>
                </Margin>
              </div>
            </div>
          ) : (
            <div
              css={mediaQueries({
                width: [350, 350, 580, 580],
                padding: 32,
              })}
            >
              <PendingInvites
                group={group}
                groupRequests={group.pendingRequests.filter(filterByTypename("VendorGroupRequest"))}
                onRequestClose={onRequestClose}
              />
            </div>
          )}
        </Modal>
      )}
    </Mutation>
  );
}

interface IPendingInvitesProps {
  group: Pick<IGroup, "_id" | "name">;
  groupRequests: NonNullable<IGroupQuery["groupBySlug"]>["pendingRequests"];
  onRequestClose?: (arg?: any) => void;
}

function PendingInvites({ group, groupRequests, onRequestClose }: IPendingInvitesProps) {
  if (!groupRequests) {
    return null;
  }

  const user = useSelfie();
  const inputRef = createRef<HTMLInputElement>();
  const [copied, setClipboard] = useClipboard(inputRef);

  if (!user) {
    return null;
  }

  return (
    <div>
      <h3
        css={{
          fontWeight: 600,
          fontSize: 18,
          color: palette.darkGray,
          margin: 0,
        }}
      >
        <FormattedMessage
          defaultMessage="Vendor Pending Invites ({invitesCount})"
          id="CgPmV1"
          values={{ invitesCount: groupRequests.length }}
        />
      </h3>

      <Mutation<ICancelGroupRequestMutation, ICancelGroupRequestMutationVariables> mutation={CancelGroupRequest}>
        {(cancelGroupRequest, { loading }) => (
          <Margin mt={24}>
            <div
              css={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, 70px)",
                gridGap: 32,
              }}
            >
              {groupRequests.map((groupRequest) => {
                if (groupRequest.__typename !== "VendorGroupRequest") {
                  return null;
                }

                const isSameHour = moment(groupRequest.createdAt).isSame(moment().toDate(), "hour");

                return (
                  <Tooltip
                    key={groupRequest._id}
                    backgroundColor={palette.innovatorBlue}
                    boxStyle={{
                      padding: "4px 8px",
                      border: "none",
                      borderRadius: 4,
                      boxShadow: "0 10px 40px 0 rgba(34, 35, 40, 0.2)",
                    }}
                    arrowSize={8}
                    render={() => (
                      <span
                        css={{
                          color: palette.white,
                          fontWeight: 500,
                          fontSize: 13,
                          lineHeight: 1.33,
                          letterSpacing: 1,
                        }}
                      >
                        {groupRequest.vendor.name}
                      </span>
                    )}
                  >
                    {({ innerRef, bind }) => (
                      <Link
                        href="/vendors/[vendorSlug]"
                        as={`/vendors/${groupRequest.vendor.slug}`}
                        passHref={true}
                        legacyBehavior={true}
                      >
                        <a
                          {...bind}
                          ref={innerRef}
                          css={{
                            position: "relative",
                            display: "block",
                            color: "unset",
                            textDecoration: "none",
                          }}
                        >
                          <div
                            role="button"
                            css={{
                              cursor: "pointer",
                              position: "absolute",
                              top: -5,
                              zIndex: 10,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "100%",
                              right: -5,
                              width: 22,
                              height: 22,
                              background: palette.red,
                            }}
                            onClick={(event) => {
                              event.preventDefault();

                              if (!loading) {
                                cancelGroupRequest({
                                  variables: {
                                    requestId: groupRequest._id,
                                  },
                                }).then(() => {
                                  if (groupRequests.length === 1 && onRequestClose) {
                                    onRequestClose();
                                  }
                                });
                              }
                            }}
                          >
                            <svg width={10} height={10}>
                              <path
                                fill="#FFF"
                                d="M6.188 5l3.566-3.565A.84.84 0 1 0 8.565.246L5 3.812 1.435.246A.84.84 0 1 0 .246 1.435L3.812 5 .246 8.565a.84.84 0 0 0 1.189 1.189L5 6.188l3.565 3.566a.84.84 0 1 0 1.189-1.189L6.188 5z"
                              />
                            </svg>
                          </div>

                          <ProfilePicture
                            size={70}
                            image={groupRequest.vendor.logo}
                            name={groupRequest.vendor.name}
                            css={{
                              display: "block",
                            }}
                          />

                          <span
                            css={{
                              display: "block",
                              marginTop: 2,
                              fontSize: 12,
                              color: palette.darkGray,
                              textAlign: "center",
                            }}
                          >
                            {isSameHour ? (
                              <FormattedMessage defaultMessage="just now" id="I90sbW" />
                            ) : (
                              <IntlRelativeTime value={groupRequest.createdAt} />
                            )}
                          </span>
                        </a>
                      </Link>
                    )}
                  </Tooltip>
                );
              })}
            </div>
          </Margin>
        )}
      </Mutation>

      <Margin mt={24}>
        <div
          css={{
            fontWeight: 500,
            fontSize: 16,
            color: palette.darkGray,
          }}
        >
          <FormattedMessage
            defaultMessage="Send this invite link to your vendor contacts to join {groupName}"
            id="LzdrAd"
            values={{ groupName: group.name }}
          />
        </div>
      </Margin>

      <Margin mt={24}>
        <input
          css={{
            boxSizing: "border-box",
            padding: 12,
            fontWeight: 500,
            fontSize: 14,
            color: palette.mediumGray,
            textAlign: "center",
            display: "block",
            fontFamily: "Courier",
            lineHeight: 1.44,
            backgroundColor: "transparent",
            border: `dashed 1px #48A0DA`,
            borderRadius: 4,
            outline: "none",
            WebkitAppearance: "none",
            width: "100%",
          }}
          value={`${getGovlaunchBaseUrl()}/signin?referral=${window.btoa(
            JSON.stringify({
              userId: user._id,
              groupId: group._id,
              type: "vendor-group-request",
            }),
          )}`}
          onChange={() => {}}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
          readOnly={true}
          ref={inputRef}
        />
      </Margin>

      <Margin
        mt={16}
        css={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <button
          css={{
            border: 0,
            outline: 0,
            background: "none",
            color: palette.primary,
            fontWeight: 500,
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            padding: 0,
            margin: 0,
          }}
          type="button"
          onClick={() => {
            setClipboard();
          }}
        >
          <FormattedMessage defaultMessage="Copy invite link" id="rtACcE" />{" "}
          <span
            css={{
              marginLeft: 4,
            }}
          >
            <ClipboardIcon />
          </span>
        </button>

        {copied && (
          <span
            css={{
              marginLeft: 6,
              color: palette.greenSuccess,
            }}
          >
            <FormattedMessage defaultMessage="Copied" id="p556q3" />!
          </span>
        )}
      </Margin>
    </div>
  );
}
